export const QWERTY_ROWS = ["QWERTYUIOP", "ASDFGHJKL", "ZXCVBNM"] as const;
export const QWERTY_LETTERS = QWERTY_ROWS.join("");
export const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

export type KeyId = string | "BACKSPACE" | "SUBMIT";

export type KeyRect = {
  id: KeyId;
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
  visualPad: number;
};

function letterKey(id: string, x: number, y: number, w: number, h: number): KeyRect {
  return { id, label: id, x, y, w, h, visualPad: 3 };
}

/** Deterministic [0, 1) from a 32-bit seed. */
export function mulberry32(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function isAlphabetPerm(letters: string): boolean {
  if (letters.length !== 26) return false;
  const up = letters.toUpperCase();
  if (!/^[A-Z]{26}$/.test(up)) return false;
  return new Set(up).size === 26;
}

/** 26 字母全盘置换，槽位仍是 QWERTY 三行。 */
export function permuteAlphabet(seed: number): string {
  const arr = ALPHABET.split("");
  const rng = mulberry32(seed >>> 0);
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = tmp;
  }
  const out = arr.join("");
  if (out === QWERTY_LETTERS) return permuteAlphabet((seed + 1) >>> 0);
  return out;
}

export function layoutQwerty(
  area: { x: number; y: number; w: number; h: number },
  letters?: string,
): KeyRect[] {
  const pad = 5;
  const rowH = (area.h - pad * 4) / 3;
  const keys: KeyRect[] = [];
  const [top, mid, bot] = QWERTY_ROWS;
  if (!top || !mid || !bot) return keys;
  const seq = letters && isAlphabetPerm(letters) ? letters.toUpperCase() : QWERTY_LETTERS;

  const keyW = (area.w - pad * (top.length + 1)) / top.length;
  const rowY = (ri: number) => area.y + pad + ri * (rowH + pad);

  let li = 0;
  for (let i = 0; i < top.length; i++) {
    keys.push(letterKey(seq[li++]!, area.x + pad + i * (keyW + pad), rowY(0), keyW, rowH));
  }

  const midW = mid.length * keyW + (mid.length - 1) * pad;
  const midX = area.x + (area.w - midW) / 2;
  for (let i = 0; i < mid.length; i++) {
    keys.push(letterKey(seq[li++]!, midX + i * (keyW + pad), rowY(1), keyW, rowH));
  }

  const lettersW = bot.length * keyW + (bot.length - 1) * pad;
  const specialW = (area.w - lettersW - pad * 4) / 2;
  const y = rowY(2);
  keys.push({
    id: "BACKSPACE",
    label: "⌫",
    x: area.x + pad,
    y,
    w: specialW,
    h: rowH,
    visualPad: 3,
  });
  const letterX = area.x + pad * 2 + specialW;
  for (let i = 0; i < bot.length; i++) {
    keys.push(letterKey(seq[li++]!, letterX + i * (keyW + pad), y, keyW, rowH));
  }
  keys.push({
    id: "SUBMIT",
    label: "提交",
    x: area.x + area.w - pad - specialW,
    y,
    w: specialW,
    h: rowH,
    visualPad: 3,
  });
  return keys;
}

export function hitTestKey(keys: readonly KeyRect[], px: number, py: number): KeyId | null {
  let best: { id: KeyId; dist: number } | null = null;
  for (const k of keys) {
    const expanded = {
      x: k.x - 2,
      y: k.y - 6,
      w: k.w + 4,
      h: k.h + 8,
    };
    if (px >= expanded.x && px <= expanded.x + expanded.w && py >= expanded.y && py <= expanded.y + expanded.h) {
      const cx = k.x + k.w / 2;
      const cy = k.y + k.h / 2;
      const dist = (px - cx) ** 2 + (py - cy) ** 2;
      if (!best || dist < best.dist) best = { id: k.id, dist };
    }
  }
  return best?.id ?? null;
}
